import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SearchUtils } from '@spotify/confidence-core-react';
import {
  ChatEventFragment,
  useChatEventCreatedSubscription,
} from '@spotify/confidence-graphql';

import { insertChatEventIntoCache } from '../insertChatEventIntoCache';
import { useChatEvents } from '../useChatEvents';

export const DEFAULT_POLL_INTERVAL_MS = 5_000;
export const CHAT_EVENTS_ORDER_BY = 'createTime:desc';
const SUBSCRIPTION_STALE_THRESHOLD_MS = 10_000;

export type UseCachedChatEventsResult = {
  chatEvents: ChatEventFragment[];
  isLoading: boolean;
  isLoadingNextPage: boolean;
  hasMoreHistory: boolean;
  loadMoreHistory: () => void;
  error?: Error;
  refetch: () => void;
};

export type UseCachedChatEventsOptions = {
  pollInterval?: number;
  autoLoadAll?: boolean;
  skipSubscription?: boolean;
};

export const useCachedChatEvents = (
  name: string,
  options?: UseCachedChatEventsOptions,
): UseCachedChatEventsResult => {
  const escapedName = SearchUtils.escapeQuery(name);
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL_MS;
  const autoLoadAll = options?.autoLoadAll ?? true;

  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const staleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setSubscriptionActive(false);
    if (staleTimeoutRef.current) clearTimeout(staleTimeoutRef.current);
    return () => {
      if (staleTimeoutRef.current) clearTimeout(staleTimeoutRef.current);
    };
  }, [name]);

  const {
    chatEvents: loadedEvents,
    hasNextPage,
    nextPage,
    isLoading,
    isLoadingNextPage,
    error,
    refetch,
  } = useChatEvents({
    parent: name,
    orderBy: CHAT_EVENTS_ORDER_BY,
    pollInterval: subscriptionActive ? 0 : pollInterval,
  });

  useChatEventCreatedSubscription({
    variables: { filter: `chatEvent.name:"${escapedName}\\/events"*` },
    skip: !name || !!options?.skipSubscription,
    onData({ data: { data: event }, client }) {
      const chatEvent = event?.chatEventCreated?.chatEvent;
      if (!chatEvent) return;
      if (!chatEventBelongsToChat(chatEvent, name)) return;

      setSubscriptionActive(true);
      clearTimeout(staleTimeoutRef.current);
      staleTimeoutRef.current = setTimeout(() => {
        setSubscriptionActive(false);
      }, SUBSCRIPTION_STALE_THRESHOLD_MS);

      insertChatEventIntoCache(client, name, chatEvent);
    },
  });

  const loadedEventsForChat = useMemo(() => {
    const eventsForChat =
      loadedEvents?.filter(event => chatEventBelongsToChat(event, name)) ?? [];
    const sortedDescending = sortChatEventsDescending(eventsForChat);

    return {
      chronological: [...sortedDescending].reverse(),
      sortedDescending,
    };
  }, [loadedEvents, name]);

  const [cachedEvents, setCachedEvents] = useState<ChatEventFragment[]>([]);
  const cachedEventsRef = useRef<ChatEventFragment[]>([]);
  const allHistoryLoadedRef = useRef(false);

  const updateCache = (next: ChatEventFragment[]) => {
    cachedEventsRef.current = next;
    setCachedEvents(next);
  };

  useEffect(() => {
    updateCache([]);
    allHistoryLoadedRef.current = false;
  }, [name]);

  useEffect(() => {
    if (!loadedEventsForChat.chronological.length) return;

    const current = cachedEventsRef.current.filter(event =>
      chatEventBelongsToChat(event, name),
    );
    const { chronological, sortedDescending } = loadedEventsForChat;
    const shouldPage =
      autoLoadAll && hasNextPage && !allHistoryLoadedRef.current;

    if (current.length === 0) {
      updateCache(chronological);
      if (shouldPage) void nextPage();
      return;
    }

    const oldestLoadedName = sortedDescending[sortedDescending.length - 1].name;
    const overlapIndex = current.findIndex(e => e.name === oldestLoadedName);

    if (overlapIndex !== -1) {
      const merged = [...current.slice(0, overlapIndex), ...chronological];
      if (
        merged.length === current.length &&
        merged.every((e, i) => e === current[i])
      ) {
        return;
      }
      updateCache(merged);
      if (shouldPage) void nextPage();
      return;
    }

    const newestLoadedName = sortedDescending[0].name;
    if (current.findIndex(e => e.name === newestLoadedName) !== -1) {
      updateCache(chronological);
      if (shouldPage) void nextPage();
      return;
    }

    updateCache([...current, ...chronological]);
    if (shouldPage) void nextPage();
  }, [loadedEventsForChat, name, autoLoadAll, hasNextPage, nextPage]);

  const cachedEventsForChat = useMemo(
    () => cachedEvents.filter(event => chatEventBelongsToChat(event, name)),
    [cachedEvents, name],
  );
  const displayedEvents =
    cachedEventsForChat.length > 0
      ? cachedEventsForChat
      : loadedEventsForChat.chronological;

  useEffect(() => {
    if (!hasNextPage && !isLoading && cachedEventsRef.current.length > 0) {
      allHistoryLoadedRef.current = true;
    }
  }, [hasNextPage, isLoading]);

  const hasMoreHistory = hasNextPage && !allHistoryLoadedRef.current;

  const loadMoreHistory = useCallback(() => {
    if (hasNextPage && !allHistoryLoadedRef.current) void nextPage();
  }, [hasNextPage, nextPage]);

  return {
    chatEvents: displayedEvents,
    isLoading,
    isLoadingNextPage,
    hasMoreHistory,
    loadMoreHistory,
    error,
    refetch,
  };
};

function chatEventBelongsToChat(
  chatEvent: ChatEventFragment,
  chatName: string,
) {
  return chatEvent.name.startsWith(`${chatName}/events/`);
}

function sortChatEventsDescending(events: ChatEventFragment[]) {
  return [...events].sort((a, b) => {
    const byTime = b.createTime.localeCompare(a.createTime);
    if (byTime !== 0) {
      return byTime;
    }

    return b.name.localeCompare(a.name);
  });
}
